import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp, theme } from 'antd';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Suppliers from './pages/Suppliers';
import SupplierDetail from './pages/SupplierDetail';
import Settings from './pages/Settings';
import WZDocuments from './pages/WZDocuments';
import WZDetail from './pages/WZDetail';
import WZNew from './pages/WZNew';
import TransportRoutes from './pages/TransportRoutes';
import CreateTransportRoute from './pages/CreateTransportRoute';
import TransportRouteDetail from './pages/TransportRouteDetail';
import EditTransportRoute from './pages/EditTransportRoute';
import Invoices from './pages/Invoices';
import PurchaseInvoiceDraftDetail from './pages/PurchaseInvoiceDraftDetail';
import SalesInvoiceDraftDetail from './pages/SalesInvoiceDraftDetail';
import DocumentCenter from './pages/DocumentCenter';
// Warehouse/Inventory pages
import PZDocuments from './pages/PZDocuments';
import PZDetail from './pages/PZDetail';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import InventoryDashboard from './pages/InventoryDashboard';
import InventoryReports from './pages/InventoryReports';
import BatchProfitability from './pages/BatchProfitability';
import BatchLedger from './pages/BatchLedger';
import Profitability from './pages/Profitability';
// Admin
import UserManagement from './pages/UserManagement';
import OperationalExceptions from './pages/OperationalExceptions';
import BoardStandalone from './pages/BoardStandalone';
import InternalTasks from './pages/InternalTasks';
import OrderCreateWizard from './pages/OrderCreateWizard';
import SupplierOrderCreateWizard from './pages/SupplierOrderCreateWizard';
import SupplierOrderDetail from './pages/SupplierOrderDetail';
// Notifications
import NotificationSettings from './pages/NotificationSettings';
import CommunicationsLogRoute from './pages/CommunicationsLogRoute';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import { useLanguage } from './hooks/useLanguage';
import { useBranding } from './hooks/useBranding';
import { FEEDBACK_DEFAULT_DURATION } from './utils/feedback';
import { ThemeModeProvider, useThemeMode } from './theme/ThemeModeContext';
import './i18n/config';
import './App.css';

function AppContent() {
  const { locale } = useLanguage();
  const { branding } = useBranding();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  
  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: branding.primaryColor,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
          borderRadius: 8,
          ...(isDark ? {
            colorBgBase: '#0f172a',
            colorBgLayout: '#0b1220',
            colorBgContainer: '#111c2e',
            colorBgElevated: '#172338',
            colorBorder: '#334155',
            colorText: '#e5e7eb',
            colorTextSecondary: '#a9b6c7',
          } : {}),
        },
      }}
      locale={locale}
    >
      {/* Safety net for any future call site that bypasses utils/feedback.ts —
          the per-severity policy lives there, not here. */}
      <AntApp message={{ duration: FEEDBACK_DEFAULT_DURATION }}>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />

          {/* Standalone Board Route */}
          <Route path="/board" element={<PrivateRoute><BoardStandalone /></PrivateRoute>} />

          {/* Protected App Routes */}
          <Route path="/app" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="tasks" element={<InternalTasks />} />
            <Route path="orders" element={<Orders />} />
            <Route path="orders/new" element={<PrivateRoute roles={['ADMIN','OPERATIONS','ORDER_STAFF','TESTER']}><OrderCreateWizard /></PrivateRoute>} />
            <Route path="supplier-orders/new" element={<PrivateRoute requireSupplierOrders><SupplierOrderCreateWizard /></PrivateRoute>} />
            <Route path="supplier-orders" element={<PrivateRoute requireSupplierOrders><Navigate to="/app/orders?orderFlow=supplier" replace /></PrivateRoute>} />
            <Route path="supplier-orders/:id" element={<PrivateRoute requireSupplierOrders><SupplierOrderDetail /></PrivateRoute>} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/:id" element={<CustomerDetail />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="suppliers/:id" element={<SupplierDetail />} />
            <Route path="transports" element={<PrivateRoute requireTransportRoutes><TransportRoutes /></PrivateRoute>} />
            <Route path="transports/new" element={<PrivateRoute requireTransportRoutes><CreateTransportRoute /></PrivateRoute>} />
            <Route path="transports/:id/edit" element={<PrivateRoute requireTransportRoutes><EditTransportRoute /></PrivateRoute>} />
            <Route path="transports/:id" element={<PrivateRoute requireTransportRoutes><TransportRouteDetail /></PrivateRoute>} />
            <Route path="reports" element={<PrivateRoute requireFinancialReports><Navigate to="/app/profitability?tab=overview" replace /></PrivateRoute>} />
            <Route path="documents" element={<PrivateRoute requireDocumentCenter><DocumentCenter /></PrivateRoute>} />
            <Route path="wz" element={<WZDocuments />} />
            <Route path="wz/new" element={<WZNew />} />
            <Route path="wz/:id" element={<WZDetail />} />
            <Route path="invoices" element={<PrivateRoute requireSalesDocuments><Invoices /></PrivateRoute>} />
            <Route path="sales-invoices" element={<Navigate to="/app/documents" replace />} />
            <Route path="sales-invoices/:id" element={<PrivateRoute requireInvoiceImports><SalesInvoiceDraftDetail /></PrivateRoute>} />
            <Route path="purchase-invoices" element={<Navigate to="/app/documents" replace />} />
            <Route path="purchase-invoices/:id" element={<PrivateRoute requirePurchaseInvoiceImports><PurchaseInvoiceDraftDetail /></PrivateRoute>} />
            {/* Warehouse/Inventory Routes */}
            <Route path="pz" element={<PrivateRoute requireWarehouseDocuments><PZDocuments /></PrivateRoute>} />
            <Route path="pz/:id" element={<PrivateRoute requireWarehouseDocuments><PZDetail /></PrivateRoute>} />
            <Route path="products" element={<PrivateRoute roles={['ADMIN', 'OPERATIONS']}><Products /></PrivateRoute>} />
            <Route path="products/:id" element={<PrivateRoute roles={['ADMIN', 'OPERATIONS']}><ProductDetail /></PrivateRoute>} />
            <Route path="inventory" element={<InventoryDashboard />} />
            <Route path="inventory/reports" element={<PrivateRoute requireSupplierPurchasePrices><InventoryReports /></PrivateRoute>} />
            {/* Static path before /:variantId catch-all — see Express route ordering note */}
            <Route path="inventory/batch-profitability" element={<PrivateRoute requireFinancialReports><BatchProfitability /></PrivateRoute>} />
            <Route path="inventory/batch-ledger" element={<PrivateRoute requireFinancialReports><BatchLedger /></PrivateRoute>} />
            <Route path="inventory/:variantId" element={<InventoryDashboard />} />
            <Route path="profitability" element={<PrivateRoute requireFinancialReports><Profitability /></PrivateRoute>} />
            {/* "Koszty stałe" merged into the Rentowność page as a tab — see AGENTS.md Company Fixed Costs */}
            <Route path="fixed-costs" element={<PrivateRoute requireFinancialReports><Navigate to="/app/profitability?tab=fixed-costs" replace /></PrivateRoute>} />
            <Route path="settings" element={<Settings />} />
            {/* Admin Routes */}
            <Route path="admin/users" element={<PrivateRoute roles={['ADMIN']}><UserManagement /></PrivateRoute>} />
            <Route path="admin/exceptions" element={<PrivateRoute roles={['ADMIN']}><OperationalExceptions /></PrivateRoute>} />
            {/* Notification Routes */}
            <Route path="communications" element={<CommunicationsLogRoute />} />
            <Route path="settings/notifications" element={<NotificationSettings />} />
            {/* Personal notification prefs merged into settings/notifications; keep old path reachable */}
            <Route path="profile/notifications" element={<Navigate to="/app/settings/notifications" replace />} />
          </Route>
        </Routes>
      </Router>
      </AntApp>
    </ConfigProvider>
  );
}

function App() {
  return (
    <ThemeModeProvider>
      <AppContent />
    </ThemeModeProvider>
  );
}

export default App;
